import { forwardRef } from 'react';
import { formatDateTime } from '../../../common/formatDateText';
import { MentionReadOnly } from '../../../components/mentions';
import {
    HiOutlinePhotograph,
    HiOutlineDocumentText,
    HiOutlineDocumentDownload,
    HiOutlineLink,
    HiOutlineReply,
    HiOutlineClock,
    HiOutlineExclamationCircle,
    HiOutlineRefresh,
} from '../../../icons';
import { formatFileSize, getAttachmentKind, ATTACHMENT_KIND_LABELS } from '../../../common/chatUploads';
import { parseChatUrlMessage } from '../../../common/urlValid';
import { ChatReplyPreview } from './ChatReplyPreview';
import pdfIcon from '../../../assets/chat/icon-pdf.png';
import wordIcon from '../../../assets/chat/icon-word.png';

const FILE_KIND_META = {
    pdf: { icon: pdfIcon, bubbleModifier: 'chat-message__bubble--file-pdf' },
    word: { icon: wordIcon, bubbleModifier: 'chat-message__bubble--file-word' },
    file: { icon: wordIcon, bubbleModifier: 'chat-message__bubble--file-generic' },
};

function FileAttachmentCard({ attachment, attachmentLabel, attachmentSize, caption }) {
    const kind = getAttachmentKind(attachment.mimeType);
    const meta = FILE_KIND_META[kind] || FILE_KIND_META.file;

    return (
        <a
            href={attachment.url}
            target="_blank"
            rel="noreferrer"
            download={attachment.fileName || undefined}
            className="chat-file-card"
            aria-label={`Descargar ${attachment.fileName || attachmentLabel}`}
        >
            <span className="chat-file-card__inner">
                <span className="chat-file-card__badge-wrap">
                    <img
                        src={meta.icon}
                        alt=""
                        className="chat-file-card__badge"
                        aria-hidden="true"
                    />
                </span>
                <span className="chat-file-card__text">
                    <span className="chat-file-card__label">
                        {attachmentLabel}
                        {attachmentSize ? ` · ${attachmentSize}` : ''}
                    </span>
                    <span className="chat-file-card__name">
                        {attachment.fileName || 'Descargar adjunto'}
                    </span>
                </span>
                <HiOutlineDocumentDownload
                    className="chat-file-card__action"
                    aria-hidden="true"
                />
                {caption && (
                    <span className="chat-file-card__caption">{caption}</span>
                )}
            </span>
        </a>
    );
}

function ChatLinkCard({ href, isOwn }) {
    const linkClass = isOwn ? 'chat-message__link--own' : 'chat-message__link--other';

    return (
        <a
            href={href}
            target="_blank"
            rel="noreferrer"
            className={`chat-message__attachment chat-message__attachment--link ${linkClass}`}
        >
            <span className="chat-message__attachment-icon-wrap">
                <HiOutlineLink className="chat-message__attachment-icon" aria-hidden="true" />
            </span>
            <span className="chat-message__attachment-text">
                <span className="chat-message__attachment-label">Enlace</span>
                <span className="chat-message__attachment-name chat-message__attachment-name--url">
                    {href}
                </span>
            </span>
        </a>
    );
}

const roleLabels = {
    profesor: 'Profesor',
    estudiante: 'Estudiante',
};

const TYPE_ICONS = {
    image: HiOutlinePhotograph,
    file: HiOutlineDocumentText,
    url: HiOutlineLink,
};

function MessageTypeIcon({ type, isOwn }) {
    const Icon = TYPE_ICONS[type];
    if (!Icon) return null;

    return (
        <span
            className={`chat-message__type-icon ${isOwn ? 'chat-message__type-icon--own' : ''}`}
            aria-hidden="true"
        >
            <Icon className="chat-message__type-icon-svg" />
        </span>
    );
}

export const MessageItem = forwardRef(({
    message,
    isOwn,
    currentUserId,
    onReply,
    onRetry,
    participants = [],
    entering = false,
}, ref) => {
    const isSending = message.status === 'sending';
    const isFailed = message.status === 'failed';
    const senderName = [message.sender?.firstName, message.sender?.lastName].filter(Boolean).join(' ');
    const senderType = message.sender?.typeUser;
    const roleLabel = roleLabels[senderType];
    const hasReply = Boolean(message.replyTo);
    const msgType = message.type || 'text';
    const linkUrl = msgType === 'url'
        ? message.content?.trim()
        : (msgType === 'text' && !message.attachments?.length
            ? parseChatUrlMessage(message.content)
            : null);
    const isLinkMessage = Boolean(linkUrl);

    const attachment = message.attachments?.[0] || null;
    const isImageMessage = msgType === 'image' && Boolean(attachment?.url);
    const isFileMessage = msgType === 'file' && Boolean(attachment?.url);
    const isBareMedia = isLinkMessage || isImageMessage || isFileMessage;
    const displayType = isLinkMessage ? 'url' : msgType;
    const showTypeIcon = displayType !== 'text' && displayType !== 'system' && displayType !== 'file';

    const attachmentLabel = attachment
        ? ATTACHMENT_KIND_LABELS[getAttachmentKind(attachment.mimeType)]
        : '';
    const attachmentSize = attachment ? formatFileSize(attachment.size) : '';
    const caption = attachment?.url && (msgType === 'image' || msgType === 'file')
        ? message.content?.trim()
        : '';

    const useOwnSoftSurface = isOwn && (hasReply || isLinkMessage);

    const mentionClass = useOwnSoftSurface
        ? 'font-semibold text-indigo-800 capitalize'
        : isOwn
            ? 'font-semibold text-white capitalize'
            : 'font-semibold text-indigo-600 capitalize';

    const attachmentKind = attachment ? getAttachmentKind(attachment.mimeType) : null;
    const fileBubbleModifier = !isBareMedia && msgType === 'file' && attachmentKind
        ? (FILE_KIND_META[attachmentKind]?.bubbleModifier || FILE_KIND_META.file.bubbleModifier)
        : '';

    const useMediaStack = hasReply && isBareMedia;

    const bubbleClass = [
        'chat-message__bubble',
        isBareMedia ? 'chat-message__bubble--bare' : (isOwn ? 'chat-message__bubble--own' : 'chat-message__bubble--other'),
        !isBareMedia && msgType === 'file' ? 'chat-message__bubble--file' : '',
        isLinkMessage ? 'chat-message__bubble--link' : '',
        fileBubbleModifier,
        hasReply ? 'chat-message__bubble--has-reply' : '',
        useMediaStack ? 'chat-message__bubble--stacked' : '',
        useOwnSoftSurface ? 'chat-message__bubble--own-soft' : '',
        isSending ? 'chat-message__bubble--sending' : '',
        isFailed ? 'chat-message__bubble--failed' : '',
        showTypeIcon && displayType !== 'file' ? 'chat-message__bubble--typed' : '',
    ].filter(Boolean).join(' ');

    const replyPreview = hasReply ? (
        <ChatReplyPreview
            replyTo={message.replyTo}
            participants={participants}
            currentUserId={currentUserId}
            isOwn={isOwn}
            variant="inline"
        />
    ) : null;

    const messageContent = (
        <div className="chat-message__content">
            {msgType === 'image' && attachment?.url ? (
                <a
                    href={attachment.url}
                    target="_blank"
                    rel="noreferrer"
                    className="chat-message__media"
                    title="Abrir imagen en una pestaña nueva"
                >
                    <img
                        src={attachment.url}
                        alt={attachment.fileName || 'Imagen adjunta'}
                        className="chat-message__image"
                        loading="lazy"
                    />
                </a>
            ) : msgType === 'file' && attachment?.url ? (
                <FileAttachmentCard
                    attachment={attachment}
                    attachmentLabel={attachmentLabel}
                    attachmentSize={attachmentSize}
                    caption={useMediaStack ? '' : caption}
                />
            ) : isLinkMessage ? (
                <ChatLinkCard href={linkUrl} isOwn={isOwn} />
            ) : (
                <p className="whitespace-pre-wrap break-words m-0">
                    <MentionReadOnly
                        content={message.content}
                        taggedUserIds={message.mentions}
                        users={participants}
                        variant="inline"
                        inlineClassName={mentionClass}
                    />
                </p>
            )}

            {caption && msgType !== 'file' && (
                <p className="chat-message__caption">{caption}</p>
            )}
        </div>
    );

    return (
        <div
            ref={ref}
            className={[
                'chat-message',
                isOwn ? 'chat-message--own' : '',
                entering ? 'chat-message--enter' : '',
            ].filter(Boolean).join(' ')}
        >
            <div className="chat-message__body">
                {!isOwn && senderName && (
                    <span className="chat-message__sender">
                        {senderName}
                        {roleLabel && senderType === 'profesor' && (
                            <span className="chat-message__role">· {roleLabel}</span>
                        )}
                    </span>
                )}

                {isOwn && (
                    <span className="chat-message__sender chat-message__sender--own">Tú</span>
                )}

                <div className="chat-message__bubble-row">
                    <div className={bubbleClass}>
                        {useMediaStack ? (
                            <div className="chat-message__stack">
                                {replyPreview}
                                {messageContent}
                            </div>
                        ) : (
                            <>
                                {replyPreview}
                                {messageContent}
                            </>
                        )}
                    </div>

                    {!isSending && !isFailed && !isOwn && (
                        <button
                            type="button"
                            onClick={() => onReply?.(message)}
                            className="chat-message__reply"
                            title="Responder"
                            aria-label="Responder"
                        >
                            <HiOutlineReply className="chat-message__reply-icon" aria-hidden="true" />
                        </button>
                    )}
                </div>

                <div className={`chat-message__meta ${isFailed ? 'chat-message__meta--failed' : ''}`}>
                    {isFailed ? (
                        <button
                            type="button"
                            onClick={() => onRetry?.(message)}
                            className="chat-message__retry"
                            title="Reintentar envío"
                        >
                            <HiOutlineExclamationCircle className="chat-message__meta-icon" aria-hidden="true" />
                            No enviado
                            <HiOutlineRefresh className="chat-message__meta-icon" aria-hidden="true" />
                        </button>
                    ) : isSending ? (
                        <span className="chat-message__meta-status">
                            <HiOutlineClock className="chat-message__meta-icon" aria-hidden="true" />
                            Enviando…
                        </span>
                    ) : (
                        <>
                            {showTypeIcon && (
                                <span className="chat-message__meta-type" title={displayType}>
                                    <MessageTypeIcon type={displayType} isOwn={false} />
                                </span>
                            )}
                            <span>{formatDateTime(message.createdAt)}</span>
                            {!isOwn ? (
                                <button
                                    type="button"
                                    onClick={() => onReply?.(message)}
                                    className="chat-message__meta-reply"
                                    title="Responder"
                                    aria-label="Responder"
                                >
                                    <HiOutlineReply className="chat-message__meta-icon" aria-hidden="true" />
                                </button>
                            ) : null}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
});

MessageItem.displayName = 'MessageItem';
